import {unwrapAPIError} from 'utils'
import {getFlightdeckBaseUrl, requestHandler} from '../HTTP'
import {HttpMethods, EndPoints} from '../HTTP/HTTP.types'
import {
  GoogleSSPMApplicationsAPIParams,
  GoogleSSPMApplicationsAPIResponseData,
  GoogleSSPMAppDetailsAPIResponseData,
  GoogleSSPMOverviewAPIResponseData,
  SSPMApplicationsAPIParams,
  SSPMApplicationsAPIResponseData,
  SSPMAppDetailsAPIParams,
  SSPMAppDetailsResponseData,
  SSPMOverviewAPIResponseData,
} from 'interfaces'

const flightDeckBaseUrl = getFlightdeckBaseUrl()
const SSPMNeuralBaseUrl = `${flightDeckBaseUrl}${EndPoints.SSPMNeural}`

const getSSPMApplications = async (data: {
  provider?: string
  params?: SSPMApplicationsAPIParams
}): Promise<SSPMApplicationsAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<SSPMApplicationsAPIResponseData>({
        method: HttpMethods.GET,
        url: `${SSPMNeuralBaseUrl}/${data.provider}${EndPoints.SSPMApps}`,
        options: {
          params: {
            ...data.params,
          },
        },
      })
    return responseData
  } catch (error) {
    throw unwrapAPIError(error)
  }
}

const getSSPMAppDetails = async (data: {
  provider?: string
  msObjectId: string
  params?: SSPMAppDetailsAPIParams
}): Promise<SSPMAppDetailsResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<SSPMAppDetailsResponseData>({
        method: HttpMethods.GET,
        url: `${SSPMNeuralBaseUrl}/${
          data.provider
        }${EndPoints.SSPMAppDetails.replace('{msObjectId}', data.msObjectId)}`,
        options: {
          params: {
            ...data.params,
          },
        },
      })
    return responseData
  } catch (error) {
    throw unwrapAPIError(error)
  }
}

const getSSPMOverview = async (data: {
  provider?: string
}): Promise<SSPMOverviewAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<SSPMOverviewAPIResponseData>({
        method: HttpMethods.GET,
        url: `${SSPMNeuralBaseUrl}/${data.provider}${EndPoints.SSPMOverview}`,
      })
    return responseData
  } catch (error) {
    throw unwrapAPIError(error)
  }
}

const GOOGLE_SSPM_BASE_URL = `${SSPMNeuralBaseUrl}/google`

const serializeGoogleSSPMApplicationsParams = (
  params: GoogleSSPMApplicationsAPIParams,
): URLSearchParams => {
  const searchParams = new URLSearchParams()

  if (params.first !== undefined) {
    searchParams.set('first', String(params.first))
  }
  if (params.after !== undefined) {
    searchParams.set('after', params.after)
  }
  if (params.category) {
    searchParams.set('category', params.category)
  }
  for (const riskLevel of params.riskLevel ?? []) {
    searchParams.append('riskLevel', riskLevel)
  }
  if (params.showNewApps !== undefined) {
    searchParams.set('showNewApps', String(params.showNewApps))
  }
  const search = params.search?.trim()
  if (search) {
    searchParams.set('search', search)
  }
  if (params.sort) {
    searchParams.set('sort', params.sort)
  }

  return searchParams
}

const getGoogleSSPMOverview =
  async (): Promise<GoogleSSPMOverviewAPIResponseData> => {
    try {
      const {data: responseData} =
        await requestHandler<GoogleSSPMOverviewAPIResponseData>({
          method: HttpMethods.GET,
          url: `${GOOGLE_SSPM_BASE_URL}${EndPoints.SSPMOverview}`,
        })
      return responseData
    } catch (error) {
      throw unwrapAPIError(error)
    }
  }

const getGoogleSSPMApplications = async (
  params: GoogleSSPMApplicationsAPIParams = {},
): Promise<GoogleSSPMApplicationsAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<GoogleSSPMApplicationsAPIResponseData>({
        method: HttpMethods.GET,
        url: `${GOOGLE_SSPM_BASE_URL}${EndPoints.SSPMApps}`,
        options: {
          params: serializeGoogleSSPMApplicationsParams(params),
          // Axios preserves commas for object params (Microsoft), but URLSearchParams encodes them.
          paramsSerializer: {
            serialize: query => query.toString().replace(/%2C/gi, ','),
          },
        },
      })
    return responseData
  } catch (error) {
    throw unwrapAPIError(error)
  }
}

const getGoogleSSPMAppDetails = async (
  clientId: string,
): Promise<GoogleSSPMAppDetailsAPIResponseData> => {
  try {
    const {data: responseData} =
      await requestHandler<GoogleSSPMAppDetailsAPIResponseData>({
        method: HttpMethods.GET,
        url: `${GOOGLE_SSPM_BASE_URL}${EndPoints.SSPMAppDetails.replace(
          '{msObjectId}',
          encodeURIComponent(clientId),
        )}`,
      })
    return responseData
  } catch (error) {
    throw unwrapAPIError(error)
  }
}

export const SSPMService = {
  getSSPMApplications,
  getSSPMAppDetails,
  getSSPMOverview,
  getGoogleSSPMOverview,
  getGoogleSSPMApplications,
  getGoogleSSPMAppDetails,
}
